import React, { useState, useMemo } from 'react';
import { Layout1 } from 'src/components/layout';
import { BreadCrumb } from 'src/components/layout/breadcrumb';
import { T } from '@transifex/react';
import { Header } from 'src/components/layout/header';
import { NotificationOutlined, SyncOutlined } from '@ant-design/icons';
import {
  Button,
  Col,
  Row,
  Grid,
  Form,
  Input,
  message,
  Alert,
  Divider,
} from 'src/antd';
import { Typography } from 'src/ui/Typography';
import { isValidPhoneNumber } from 'react-phone-number-input';
import { InputPhone } from 'src/components/shared/InputPhone';
import Captcha from 'src/components/shared/Captcha';
import { VALIDATION_MESSAGE } from 'src/utils/validation';
import { apiNotification } from 'src/api/notifications';
import { useGetToken } from 'src/api/config';
import { apiErrorUtils } from 'src/api/consts/error';
import { useHistory } from 'react-router';
import { routingMap } from 'src/routing/routing-map';
import { setCookie } from 'src/utils/cookies';
import {
  emailCodeCookie,
  emailServiceAddress,
  emailServiceMode,
  smsCodeCookie,
  smsServiceMode,
  smsServicePhoneNumber,
} from 'src/consts/cookie-consts';
import NotificationRadio from '../components/NotificationRadio';
import useCountdownTimer from '../components/useCountdownTimer';
import { TNotificationMode } from '../store';
import { notificationModeContact2Map } from '../components/consts';

const Notifications = () => {
  const [selectedType, setSelectedType] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [form] = Form.useForm();
  const [captchaId, setCaptchaId] = useState(0);
  const [currentStep, setCurrentStep] = useState<0 | 1>(0);
  const [timeoutTimestamp, setTimeoutTimestamp] = useState(0);
  const resendCodeTimer = useCountdownTimer(timeoutTimestamp);
  const token = useGetToken();
  const history = useHistory();

  const { xs, xl } = Grid.useBreakpoint();

  const initialValues = useMemo(
    () => ({
      phoneNumber: '',
      email: '',
      captchaToken: '',
      notificationCode: '',
    }),
    []
  );

  const sendCode = async () => {
    const { phoneNumber, email, captchaToken } = form.getFieldsValue();

    const { data } = await apiNotification
      .mutateSendVerificationCode({
        token,
        verification_type: selectedType as 'sms' | 'email',
        email,
        phone_number: phoneNumber,
        captcha_token: captchaToken,
      })
      .finally(() => {
        setCaptchaId((value) => value + 1);
      });

    setTimeoutTimestamp(data.next_attempt_timestamp || 0);

    if (data) {
      setCurrentStep(1);
      message.success(<T _str="Verification code sent" />);
    } else {
      message.error(<T _str="Error occured" />);
    }
  };

  const verifyCode = async () => {
    const { phoneNumber, email, notificationCode } = form.getFieldsValue();

    const { data }: $TSFixMe = await apiNotification.mutateVertifyCode({
      token,
      verification_type: selectedType as 'sms' | 'email',
      email,
      phone_number: phoneNumber,
      code: notificationCode,
    });

    if (data?.verification_id) {
      const codeCookie =
        selectedType === 'sms' ? smsCodeCookie : emailCodeCookie;
      const serviceValueCookie =
        selectedType === 'sms' ? smsServicePhoneNumber : emailServiceAddress;
      const serviceModeCookie =
        selectedType === 'sms' ? smsServiceMode : emailServiceMode;

      setCookie(codeCookie, data.verification_id, 1 / 24);
      setCookie(serviceValueCookie, phoneNumber || email || '', 1 / 24);
      setCookie(serviceModeCookie, selectedType, 1 / 24);

      message.success(<T _str="Code verified successfully" />);
      if (selectedType === 'sms') {
        history.push(routingMap.app.notifications.sms._);
      } else if (selectedType === 'email') {
        history.push(routingMap.app.notifications.email._);
      }
    } else {
      message.error(<T _str="Invalid verification code" />);
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      if (currentStep === 0) {
        await sendCode();
      } else {
        await verifyCode();
      }
    } catch (e) {
      apiErrorUtils.handleError(e);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    setCurrentStep(0);
    setSelectedType('');
    form.setFieldsValue({ notificationCode: '', captchaToken: '' });
    setCaptchaId((value) => value + 1);
  };

  return (
    <Layout1
      breadcrumb={
        <BreadCrumb
          title={<T _str="Subscription management" />}
          onBack={currentStep === 1 ? handleBack : undefined}
        />
      }
      header={
        <Header
          icon={<NotificationOutlined />}
          title={<T _str="SMS/Email service" />}
        />
      }
      mainContent={
        <Row gutter={[xs ? 16 : 32, xs ? 16 : 32]}>
          <Col xs={24} md={24} xl={12} style={{ order: xl ? 2 : -1 }}>
            {currentStep === 0 && (
              <Alert
                showIcon
                type="info"
                message={
                  <Typography variant="h6" weight="semi-bold">
                    <T _str="Getting started" />
                  </Typography>
                }
                description={
                  <T _str="Before you can subscribe to container notifications, we need to validate your contact information. Please enter your phone number or email address on the left and you'll receive a verification code within a couple of minutes. Enter that code to validate your contact method, then you'll be able to select which containers you want to receive notifications for." />
                }
              />
            )}
          </Col>
          <Col xs={24} md={24} xl={12} style={{ order: xl ? 1 : 0 }}>
            <div>
              {currentStep === 0 && (
                <>
                  <Typography
                    variant="body-14"
                    style={{ marginBottom: 16, display: 'inline-block' }}
                    ellipsis={false}
                  >
                    <T _str="Enter your phone number or email to manage container notifications." />
                  </Typography>
                  <NotificationRadio
                    value={selectedType}
                    onChange={setSelectedType}
                    style={{ marginBottom: 24 }}
                  />
                </>
              )}
              {selectedType && (
                <Form
                  form={form}
                  initialValues={initialValues}
                  onFinish={handleSubmit}
                  layout="vertical"
                >
                  {selectedType === 'sms' && (
                    <Form.Item
                      name="phoneNumber"
                      label={<T _str="Your mobile number" />}
                      rules={[
                        {
                          required: true,
                          message: VALIDATION_MESSAGE.REQUIRED,
                        },
                        ({ getFieldValue }) => ({
                          validator: () => {
                            const phoneNumber = getFieldValue('phoneNumber');
                            if (isValidPhoneNumber(phoneNumber))
                              return Promise.resolve();
                            return Promise.reject();
                          },
                          message: VALIDATION_MESSAGE.PHONE_NUMBER,
                        }),
                      ]}
                    >
                      <InputPhone
                        style={{ width: '100%' }}
                        disabled={currentStep === 1 || isSubmitting}
                      />
                    </Form.Item>
                  )}

                  {selectedType === 'email' && (
                    <Form.Item
                      name="email"
                      label={<T _str="Your email address" />}
                      rules={[
                        {
                          required: true,
                          message: VALIDATION_MESSAGE.REQUIRED,
                        },
                        { type: 'email', message: VALIDATION_MESSAGE.EMAIL },
                      ]}
                    >
                      <Input
                        style={{ width: '100%' }}
                        type="email"
                        disabled={currentStep === 1 || isSubmitting}
                      />
                    </Form.Item>
                  )}

                  <Form.Item
                    name="captchaToken"
                    label={<T _str="Please verify that you are not a robot" />}
                    rules={[
                      {
                        required: true,
                        message: VALIDATION_MESSAGE.REQUIRED,
                      },
                    ]}
                  >
                    <Captcha key={`captcha-${captchaId}`} />
                  </Form.Item>

                  {currentStep === 1 && (
                    <>
                      <Divider />
                      <Alert
                        style={{ marginBottom: 16 }}
                        showIcon
                        type="info"
                        message={
                          <Typography variant="h6" weight="semi-bold">
                            <T _str="We have sent you the code." />
                          </Typography>
                        }
                        description={
                          <T
                            _str="We have sent you a code to your {method}. Please enter the code you received to verify. It might take up to 5 minutes before you receive it. If you didn't receive the code, please make sure the contact information you put in is correct."
                            method={
                              notificationModeContact2Map[
                                selectedType as TNotificationMode
                              ]
                            }
                          />
                        }
                      />
                      <Form.Item
                        name="notificationCode"
                        label={<T _str="Verification code" />}
                        rules={[
                          {
                            required: true,
                            message: VALIDATION_MESSAGE.REQUIRED,
                          },
                        ]}
                      >
                        <Input
                          style={{ width: '100%' }}
                          disabled={isSubmitting}
                          placeholder="Enter verification code"
                        />
                      </Form.Item>
                    </>
                  )}

                  {currentStep === 1 ? (
                    <div style={{ display: 'flex', gap: 8 }}>
                      <Button
                        icon={<SyncOutlined />}
                        onClick={sendCode}
                        disabled={isSubmitting || !!resendCodeTimer}
                        loading={isSubmitting}
                      >
                        <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                          {resendCodeTimer ? (
                            <T
                              _str="Resend code {countdown}"
                              countdown={resendCodeTimer}
                            />
                          ) : (
                            <T _str="Resend code" />
                          )}
                        </span>
                      </Button>
                      <Form.Item shouldUpdate noStyle style={{ flex: 1 }}>
                        {({ isFieldsTouched }) => (
                          <Button
                            htmlType="submit"
                            type="primary"
                            loading={isSubmitting}
                            disabled={!isFieldsTouched() || isSubmitting}
                            style={{ width: '100%' }}
                          >
                            <T _str="Verify" />
                          </Button>
                        )}
                      </Form.Item>
                    </div>
                  ) : (
                    <Form.Item shouldUpdate noStyle>
                      {({ isFieldsTouched }) => (
                        <Button
                          htmlType="submit"
                          type="primary"
                          loading={isSubmitting}
                          disabled={!isFieldsTouched() || isSubmitting}
                          style={{ width: '100%' }}
                        >
                          <T _str="Send me a verification code" />
                        </Button>
                      )}
                    </Form.Item>
                  )}
                </Form>
              )}
            </div>
          </Col>
        </Row>
      }
    />
  );
};

export default Notifications;
